import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';

const { FiDollarSign, FiPercent, FiCalendar, FiPieChart, FiArrowRight, FiInfo } = FiIcons;

const LoanCalculator = ({ initialPrice = 45000 }) => {
  const [price, setPrice] = useState(initialPrice);
  const [downPayment, setDownPayment] = useState(5000);
  const [tradeValue, setTradeValue] = useState(0);
  const [term, setTerm] = useState(60);
  const [interestRate, setInterestRate] = useState(5.9);

  const stats = useMemo(() => {
    const principal = Math.max(0, price - downPayment - tradeValue);
    const monthlyRate = interestRate / 100 / 12;
    const n = term;
    
    let monthlyPayment = 0;
    if (monthlyRate > 0) {
      monthlyPayment = (principal * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    } else {
      monthlyPayment = principal / n;
    }

    const totalCost = monthlyPayment * n;
    const totalInterest = totalCost - principal;

    return {
      monthlyPayment: isNaN(monthlyPayment) ? 0 : monthlyPayment,
      totalInterest: isNaN(totalInterest) ? 0 : totalInterest,
      principal: principal,
      totalLoan: totalCost
    };
  }, [price, downPayment, tradeValue, term, interestRate]);

  const chartOption = {
    tooltip: { trigger: 'item' },
    legend: { bottom: '0%', textStyle: { color: '#9ca3af' } },
    series: [
      {
        name: 'Loan Breakdown',
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#1f2937',
          borderWidth: 2
        },
        label: { show: false },
        emphasis: {
          label: {
            show: true,
            fontSize: '14',
            fontWeight: 'bold',
            color: '#fff'
          }
        },
        data: [
          { value: stats.principal, name: 'Principal', itemStyle: { color: '#2563eb' } },
          { value: stats.totalInterest, name: 'Interest', itemStyle: { color: '#ef4444' } },
          { value: downPayment + tradeValue, name: 'Down/Trade', itemStyle: { color: '#10b981' } }
        ]
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden shadow-2xl"
    >
      <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
        <h3 className="text-xl font-bold text-white flex items-center">
          <SafeIcon icon={FiPieChart} className="w-5 h-5 mr-2 text-blue-500" />
          Premium Loan Calculator
        </h3>
        <p className="text-sm text-gray-400">Estimate your monthly payments with Carson Exports</p>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Controls */}
        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-gray-300">Vehicle Price</label>
                <span className="text-sm font-bold text-white">${price.toLocaleString()}</span>
              </div>
              <input 
                type="range" min="5000" max="150000" step="1000"
                value={price} onChange={(e) => setPrice(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wider font-bold">Down Payment</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input 
                    type="number" value={downPayment} onChange={(e) => setDownPayment(Number(e.target.value))}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-600 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wider font-bold">Trade-In Value</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input 
                    type="number" value={tradeValue} onChange={(e) => setTradeValue(Number(e.target.value))}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-600 outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wider font-bold">Term (Months)</label>
                <select 
                  value={term} onChange={(e) => setTerm(Number(e.target.value))}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-600 outline-none"
                >
                  {[24, 36, 48, 60, 72, 84].map(m => <option key={m} value={m}>{m} Months</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1 uppercase tracking-wider font-bold">Interest Rate (%)</label>
                <div className="relative">
                  <input 
                    type="number" step="0.1" value={interestRate} onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-600 outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 bg-blue-600/10 border border-blue-500/20 rounded-xl">
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-500 mt-0.5" />
              <p className="text-xs text-gray-400 leading-relaxed">
                Rates are estimated based on current market averages. Final rates are subject to credit approval by our lending partners.
              </p>
            </div>
          </div>
        </div>

        {/* Results & Chart */}
        <div className="flex flex-col items-center justify-center">
          <div className="text-center mb-6">
            <span className="text-sm text-gray-400 uppercase tracking-widest font-bold">Estimated Monthly Payment</span>
            <div className="text-5xl font-black text-white mt-2">
              <span className="text-3xl font-bold text-blue-500 mr-1">$</span>
              {stats.monthlyPayment.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>

          <div className="w-full h-48">
            <ReactECharts option={chartOption} style={{ height: '100%' }} />
          </div>

          <div className="w-full mt-6 space-y-3">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold flex items-center justify-center space-x-2 shadow-lg shadow-blue-600/20"
            >
              <span>Apply for Pre-Approval</span>
              <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default LoanCalculator;